import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Route, Trash2, Save, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const blank = { name: "", lat: "", lon: "", kind: "city" };

function Wp({ label, value, onChange }) {
  const v = value || { name: "", lat: "", lon: "" };
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="grid grid-cols-3 gap-2">
      <input data-testid={`route-${label}-name`} className="border rounded-lg px-2 py-1.5 text-sm" placeholder={`${label} city`} value={v.name} onChange={(e) => set("name", e.target.value)} />
      <input className="border rounded-lg px-2 py-1.5 text-sm" placeholder="lat" value={v.lat ?? ""} onChange={(e) => set("lat", e.target.value)} />
      <input className="border rounded-lg px-2 py-1.5 text-sm" placeholder="lon" value={v.lon ?? ""} onChange={(e) => set("lon", e.target.value)} />
    </div>
  );
}

export default function TripRouteEditor({ trip, onSaved }) {
  const [open, setOpen] = useState((trip.cities || []).length === 0);
  const [templates, setTemplates] = useState([]);
  const [origin, setOrigin] = useState(trip.origin || null);
  const [destination, setDestination] = useState(trip.destination || null);
  const [crossing, setCrossing] = useState(trip.crossing || null);
  const [cities, setCities] = useState((trip.cities || []).map((c) => ({ name: c.name, lat: c.lat, lon: c.lon, kind: c.kind })));
  const [saving, setSaving] = useState(false);
  const empty = (trip.cities || []).length === 0 && !trip.origin && !trip.destination;

  useEffect(() => { api.get("/admin/route-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setOrigin(t.origin); setDestination(t.destination); setCrossing(t.crossing);
    setCities((t.cities || []).map((c) => ({ name: c.name, lat: c.lat, lon: c.lon, kind: c.kind })));
    toast.success(`Loaded "${t.name}" — review and save`);
  };

  const toWp = (w) => (w && w.name ? { name: w.name, lat: parseFloat(w.lat), lon: parseFloat(w.lon) } : null);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/trips/${trip.id}`, {
        origin: toWp(origin), destination: toWp(destination),
        crossing: crossing && crossing.name ? { name: crossing.name, direction: crossing.direction || "", lat: crossing.lat ? parseFloat(crossing.lat) : null, lon: crossing.lon ? parseFloat(crossing.lon) : null } : null,
        cities: cities.filter((c) => c.name && c.lat && c.lon).map((c, i) => ({ name: c.name, lat: parseFloat(c.lat), lon: parseFloat(c.lon), kind: c.kind, order: i })),
      });
      toast.success("Route saved — the map will now show the planned journey");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className={`rounded-2xl border p-4 ${empty ? "border-amber-300 bg-amber-50" : "border-sand-200 bg-white"}`} data-testid="route-editor">
      <button className="w-full flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
        {empty ? <AlertTriangle size={16} className="text-amber-600" /> : <Route size={16} className="text-terracotta" />}
        <span className="font-medium text-charcoal-800">{empty ? "No route set — add cities to show the map" : `Planned route (${(trip.cities || []).length} stops)`}</span>
        <span className="ml-auto text-xs text-charcoal-400">{open ? "Hide" : "Edit route"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-charcoal-500">Start from a template:</span>
            <select data-testid="route-template-select" className="border rounded-lg px-2 py-1.5 text-sm" defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
              <option value="" disabled>Choose a route template…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><div className="text-xs font-medium text-charcoal-500 mb-1">Origin</div><Wp label="Origin" value={origin} onChange={setOrigin} /></div>
            <div><div className="text-xs font-medium text-charcoal-500 mb-1">Destination</div><Wp label="Destination" value={destination} onChange={setDestination} /></div>
          </div>
          <div>
            <div className="text-xs font-medium text-charcoal-500 mb-1">Cities & crossings (in order)</div>
            <div className="space-y-2">
              {cities.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input className="col-span-5 border rounded-lg px-2 py-1.5 text-sm" placeholder="City / bridge" value={c.name} onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <input className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" placeholder="lat" value={c.lat ?? ""} onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, lat: e.target.value } : x))} />
                  <input className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" placeholder="lon" value={c.lon ?? ""} onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, lon: e.target.value } : x))} />
                  <select className="col-span-2 border rounded-lg px-1 py-1.5 text-sm" value={c.kind} onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, kind: e.target.value } : x))}>
                    <option value="city">city</option><option value="border">border</option>
                  </select>
                  <button className="col-span-1 text-charcoal-400 hover:text-red-600" onClick={() => setCities(cities.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
                </div>
              ))}
              <button data-testid="route-add-city" className="text-sm text-terracotta font-medium" onClick={() => setCities([...cities, { ...blank }])}>+ Add city / crossing</button>
            </div>
          </div>
          <button data-testid="route-save-btn" onClick={save} disabled={saving} className="bg-terracotta text-white rounded-full px-5 py-2 text-sm flex items-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save route
          </button>
        </div>
      )}
    </div>
  );
}
